import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { Employee } from 'src/app/employee/models/Employee';
import { EmployeeDateTime } from 'src/app/employee/models/EmployeeDateTime';
import { AttendanceService } from 'src/app/employee/services/attendance.service';
import { EmployeeDatetimeType } from 'src/app/setting/models/EmployeeDatetimeType';
import { DatetimesTypesStoreService } from 'src/app/setting/services/datetimes-types-store.service';
import { SummernoteHelper } from 'src/app/shared/common/SummernoteHelper';
import { DateTimeProject } from '../../../../setting/models/DatetimeProject';
import { DatetimeProjectService } from '../../../../setting/services/datetime-project.service';
import { ParentOfComponent } from '../../../../ui/parent-component/components/parent-of.component';
import { AttendanceApprovers } from '../../../models/AttendanceApprovers';

export interface InfoResponse {
    drawn_units: string;
    remaining_units: string;
    holidays_count: string;
    weekend_days_count: string;
    overflow_alert: boolean;
    has_waiting_units?: boolean;
    waiting_units?: string;
    attendance_lock: {
        locked: boolean;
    };
    parent_lock: {
        locked: boolean;
        datetime_type_name?: string;
        parent_datetime_type_ID?: number;
        parent_datetime_type_name?: string;
        parent_hours?: number;
        used_parent_hours?: number
    };
    default_set_on: 'TIME' | 'SHIFT' | 'HALF_SHIFT';
    can_create: boolean;
    can_create_with_confirmation: boolean;
}

@Component({
    selector: 'app-employee-attendance-info',
    templateUrl: './create-edit-employee-datetime-modal.component.html',
    styleUrls: ['./create-edit-employee-datetime-modal.component.css']
})
export class CreateEditEmployeeDatetimeModalComponent extends ParentOfComponent implements OnInit {
    public employeeDateTime: EmployeeDateTime;
    public employeeID: number;
    public originalEmployeeID: number;
    public dateTimeEditForm: UntypedFormGroup;
    public datetimeTypes$: Observable<EmployeeDatetimeType[]>;
    public employeeDatetimeInfo$: Observable<InfoResponse>;
    public enableTime = false;
    public end: string;
    public minDate = '2020-01-01';
    public selectedType: EmployeeDatetimeType;
    public start: string;
    public approvers$: Observable<AttendanceApprovers>;
    public employees$: Observable<Employee[]>;
    public datetimeProjects$: Observable<DateTimeProject[]>;
    public allTypes: EmployeeDatetimeType[];
    public lastFormValue: any;
    public projectsEnabled = this.authService.loggedUser?.attendance_projects?.active;
    public submitted = false;
    public isTimer = false;
    public isShift = false;
    public isEmployeeProfile = false;
    public isPlan = false;
    public isLoading = false;
    public canCreate = false;
    public canCreateWithConfirmation = false;
    public canSetState = false;
    public isConfirmed = false;
    public showOverflowAlert = false;
    public showLockedAlert = false;
    public showParentLockedAlert = false;
    public presetTime = false;
    public showEmployeeSelect = false;
    public defaultSetOnPreset = false;
    public datetimeTypesLoading = true;
    public isApprovable = true;
    public editEnabled = true;
    public isFullCalendar = false;

    public constructor(
        public activeModal: NgbActiveModal,
        private _attendanceService: AttendanceService,
        private _datetimeTypesStore: DatetimesTypesStoreService,
        private _datetimeProjectService: DatetimeProjectService,
        private _cdr: ChangeDetectorRef
    ) {
        super();
    }

    public get f(): { [formControlName: string]: AbstractControl; } {
        return this.dateTimeEditForm.controls;
    }

    private _employee: Employee;

    public get employee(): Employee {
        return this._employee;
    }

    public set employee(employee: Employee) {
        this._employee = employee;
        this.employeeID = this._employee.employee_ID;
    }

    // set on shift je znegovan z důvodu změny hlášky na "chci nastavit i čas" = jak jsme se bavili - Honza bude vedet

    public ngOnInit(): void {
        if (!this.isLoading) {
            this.init();
        }
    }

    public init(): void {
        this._checkPermissionForSelectedEmployee();

        this.dateTimeEditForm = this.fb.group({
            employee_datetime_type_ID: [1, Validators.required],
            from: ['', Validators.required],
            to: ['', Validators.required],
            set_on: ['SHIFT', Validators.required],
            note: [''],
            report: [''],
            state: ['NEW', Validators.required],
            half: ['FIRST', Validators.required]
        });

        if (this.projectsEnabled) {
            this.dateTimeEditForm.addControl('employee_datetime_project_ID', this.fb.control(null));
        }

        if (this.employeeDateTime) {
            this.dateTimeEditForm.patchValue({
                employee_datetime_type_ID: this.employeeDateTime.type.employee_datetime_type_ID,
                from: this.employeeDateTime.from,
                to: this.employeeDateTime.to,
                note: this.employeeDateTime.note,
                report: this.employeeDateTime.report,
                state: this.employeeDateTime.state,
                set_on: this.employeeDateTime.set_on,
                half: this.employeeDateTime.half ?? 'FIRST'
            });

            if (this.projectsEnabled) {
                this.dateTimeEditForm.patchValue({employee_datetime_project_ID: this.employeeDateTime.employee_datetime_project_ID});
            }

            this.enableTime = this.employeeDateTime.set_on === 'TIME';

            if (this.authService.checkPermission(['employeeDatetime.edit', 'employeeDatetime.editStructure'])) {
                this.dateTimeEditForm.enable();
                if (this.isFullCalendar) {
                    this.showEmployeeSelect = true;
                }
            }

            if (this.employeeDateTime.is_protected) {
                this.dateTimeEditForm.disable();
                this.editEnabled = false;
                this.showLockedAlert = true;
            }

            if (this.employeeDateTime.state === 'TO_DELETE') {
                this.dateTimeEditForm.disable();
            }

            this.originalEmployeeID = this.employeeID;
        }

        if (!this.employeeDateTime) {
            if (this.start && this.end) {
                this.dateTimeEditForm.patchValue({
                    from: this.start,
                    to: this.end,
                    set_on: this.presetTime ? 'TIME' : 'SHIFT',
                });
                this.enableTime = this.presetTime;
                if (!this.employeeID) {
                    this.showEmployeeSelect = true;
                }
            }
        }

        this.subscriptions.add(this.dateTimeEditForm.get('set_on').valueChanges
            .subscribe(val => {
                this.enableTime = val === 'TIME';
            }));

        this.subscriptions.add(this.dateTimeEditForm.valueChanges
            .subscribe(val => {
                const fromChanged = val.from !== this.lastFormValue?.from;
                const toChanged = val.to !== this.lastFormValue?.to;
                const typeChanged = val.employee_datetime_type_ID !== this.lastFormValue?.employee_datetime_type_ID;
                const setOnChanged = val.set_on !== this.lastFormValue?.set_on;

                if (fromChanged || toChanged || typeChanged || setOnChanged) {
                    if (val.from && val.to) {
                        this.minDate = val.from;

                        if (this.employeeID) {
                            this.getEmployeeDatetimeInfoBeforeSave();
                        }
                    } else if (val.from && !val.to) {
                        this.dateTimeEditForm.patchValue({
                            to: val.from
                        });
                    }
                }

                this.lastFormValue = val;
            }));

        this.datetimeTypes$ = this._datetimeTypesStore.datetimeTypes$
            .pipe(
                map(types => {
                    types = types.filter(t => t.employee_datetime_type_ID !== 7 && (this.isShift ? t.is_shift === this.isShift : t.can_plan === true) && t.is_active);
                    let selectedType = types.find(t => t.employee_datetime_type_ID === 5);

                    // preset default values and types
                    if (!this.employeeDateTime && !this.isTimer && !this.isEmployeeProfile && !this.isShift && this.isPlan) {
                        types = types.filter(t => t.can_plan);
                    }

                    if (this.isEmployeeProfile && !this.employeeDateTime) {
                        selectedType = types.find(t => t.employee_datetime_type_ID === 1);
                    }

                    if (this.employeeDateTime && this.employeeDateTime.type.employee_datetime_type_ID !== 7) {
                        selectedType = types.find(t => t.employee_datetime_type_ID === this.employeeDateTime?.type?.employee_datetime_type_ID);
                    }

                    if (this.isTimer) {
                        types = types.filter(t => t.timer);
                        selectedType = types.find(t => t.employee_datetime_type_ID === 1);
                    }

                    if (this.isShift) {
                        if (!this.employeeDateTime) {
                            selectedType = types.find(t => t.is_shift === this.isShift);
                        }
                    }

                    this.dateTimeEditForm.patchValue({employee_datetime_type_ID: selectedType?.employee_datetime_type_ID});

                    if (selectedType) {
                        this.onSelectedType(selectedType);
                    }

                    this.allTypes = types;

                    // preset last selected datetime type and default setOn, when creating new datetime
                    if (!this.employeeDateTime && this.employeeID) {
                        this._attendanceService.getLatestDatetimeType(this.isShift ? 'shift' : 'attendance', [this.employeeID], this.allTypes.map(type => type.employee_datetime_type_ID as number))
                            .then((lastTypeID) => {
                                if (lastTypeID) {
                                    this.dateTimeEditForm.patchValue({employee_datetime_type_ID: lastTypeID});
                                }
                                this.onSetOnChange();
                                this.datetimeTypesLoading = false;
                                this._cdr.detectChanges();
                            });

                    } else {
                        this.datetimeTypesLoading = false;
                    }

                    return types;
                }));

        this.employees$ = this.employeeService.getAllEmployees(false, 'select', null, null, null, null, 'employeeDatetime.create|employeeDatetime.createStructure|employeeDatetime.createOwn')
            .pipe(tap(employees => {
                // if count of returned employees is 1, we can set employeeID directly
                if (employees.length === 1) {
                    this.employeeID = employees[0].employee_ID;
                    this.onEmployeeChange();
                }
            }));

        if (this.employeeID) {
            this.refreshApprovers();
        }
        this.datetimeProjects$ = this._datetimeProjectService.getDatetimeProjectsForSelect();
    }

    public refreshApprovers(): void {
        this.approvers$ = this.employeeService.getAttendaceApproversByEmployee(this.employeeID);
    }

    public deleteDateTime(): void {
        this._attendanceService.deleteEmployeeDateTimeByID(this.employeeID, this.employeeDateTime.employee_datetime_ID, !this.employeeDateTime.permissions.can_set_state && this.employeeDateTime.state !== 'NEW')
            .then(() => this.activeModal.close('cancel'))
            .catch(() => { });
    }

    public revokeDelete(): void {
        this._attendanceService.revokeEmployeeDateTimeByID(this.employeeID, this.employeeDateTime.employee_datetime_ID)
            .then(() => this.activeModal.close('cancel'))
            .catch(() => { });
    }

    public getEmployeeDatetimeInfoBeforeSave(): void {
        const modifiedForm = {
            ...this.dateTimeEditForm.value,
            set_on: this.dateTimeEditForm.value.set_on,
        };
        this.employeeDatetimeInfo$ = this._attendanceService.getEmployeeDateTimeInfoBeforeSave(this.employeeID, modifiedForm)
            .pipe(
                tap((info: InfoResponse) => {
                    if (!this.presetTime && info.default_set_on && !this.defaultSetOnPreset && !this.employeeDateTime?.employee_datetime_ID) {
                        this.dateTimeEditForm.patchValue({set_on: info.default_set_on});
                        this.onSetOnChange();
                        this.defaultSetOnPreset = true;
                        this._cdr.detectChanges();
                    }
                    this.canCreate = info.can_create || false;
                    this.canCreateWithConfirmation = info.can_create_with_confirmation || false;
                    this.showOverflowAlert = info.overflow_alert || false;
                    this.showParentLockedAlert = info.parent_lock.locked;
                    if (info.attendance_lock.locked) {
                        this.editEnabled = false;
                        this.showLockedAlert = true;
                    }
                }),
                shareReplay());
    }

    public onSelectedType(type: EmployeeDatetimeType): void {
        this.selectedType = type;
        this.isApprovable = type.is_approvable;
        if (!this.employeeDateTime) {
            if (type.is_approvable) {
                this.dateTimeEditForm.patchValue({state: 'NEW'});
                this.dateTimeEditForm.get('state').enable();
            } else {
                this.dateTimeEditForm.patchValue({state: 'APPROVED'});
                this.dateTimeEditForm.get('state').disable();
            }
            if (this.canSetState) {
                this.dateTimeEditForm.patchValue({state: 'APPROVED'});
            }
        }
        if (this.dateTimeEditForm.value.set_on === 'TIME' && type.custom_from_to?.from && type.custom_from_to?.to && this.isShift) {
            this.dateTimeEditForm.patchValue({
                from: this.dateTimeEditForm.value.from.substring(0, 10) + 'T' + type.custom_from_to?.from,
                to: this.dateTimeEditForm.value.to.substring(0, 10) + 'T' + type.custom_from_to?.to,
            });
        } else {
            const endIndex = this.dateTimeEditForm.value.set_on === 'TIME' ? 16 : 10;
            this.dateTimeEditForm.patchValue({
                from: this.dateTimeEditForm.value.from.substring(0, endIndex),
                to: this.dateTimeEditForm.value.to.substring(0, endIndex),
            });
        }
    }

    public onSubmit(): void {
        this.submitted = true;

        if (!this.dateTimeEditForm.valid) {
            return;
        }

        const value = this.dateTimeEditForm.value;

        if (value.set_on !== 'TIME') {
            value.from = value.from.substring(0, 10);
            value.to = value.to.substring(0, 10);
        }

        if (value.set_on !== 'HALF_SHIFT') {
            delete value.half;
        }

        let promise = null;

        if (Object.keys(value).length === 1) {
            promise = this._attendanceService.saveEmployeeDateTimeState(value, this.employeeID);
        } else if (this.employeeDateTime) {
            if (this.originalEmployeeID !== this.employeeID) {
                value.employee_ID = this.employeeID;
            }
            promise = this._attendanceService.saveEmployeeDateTime(value, this.originalEmployeeID, this.employeeDateTime.employee_datetime_ID, this.isShift ? 'SHIFT' : 'ATTENDANCE', this.isFullCalendar);
        } else {
            promise = this._attendanceService.saveEmployeeDateTime(value, this.employeeID, null, this.isShift ? 'SHIFT' : 'ATTENDANCE', this.isFullCalendar);
        }

        promise.then((response) => this.activeModal.close(response));
    }

    public onConfirmedChange($event: any): void {
        this.isConfirmed = $event.target.checked;
    }

    public onSetOnChange(): void {
        if (this.dateTimeEditForm.value.set_on === 'TIME') {
            const selectedDatetimeType = this.allTypes.find(x => x.employee_datetime_type_ID === this.dateTimeEditForm.value.employee_datetime_type_ID);
            this.onSelectedType(selectedDatetimeType);
        }
    }

    public onEmployeeChange(): void {
        this.refreshApprovers();
        this._checkPermissionForSelectedEmployee();
        this.datetimeTypesLoading = true;
        this._attendanceService.getLatestDatetimeType(this.isShift ? 'shift' : 'attendance', [this.employeeID], this.allTypes.map(type => type.employee_datetime_type_ID as number))
            .then(lastTypeID => {
                if (lastTypeID) {
                    this.dateTimeEditForm.patchValue({employee_datetime_type_ID: lastTypeID});
                }
                this.getEmployeeDatetimeInfoBeforeSave();
                this.datetimeTypesLoading = false;
                this._cdr.detectChanges();
            });
    }

    private _checkPermissionForSelectedEmployee(): void {
        if (this.employeeID) {
            this.subscriptions.add(this.authService.hasPermissionForSelectedObject(['employeeDatetime.approveOrDeny', 'employeeDatetime.approveOrDenyStructure'], 'App\\Models\\Employee', this.employeeID)
                .subscribe(allowed => {
                    this.canSetState = allowed;
                    if (allowed) {
                        if (!this.employeeDateTime) {
                            this.dateTimeEditForm.patchValue({state: 'APPROVED'});
                        }
                        this.f.state.enable();
                    } else {
                        this.f.state.disable();
                    }
                }));
        }
    }
}
